// Performance Monitoring and Core Web Vitals Tracking
// Custom Design Tool - Customer Manager

// @ts-ignore - web-vitals types may not be available
// Temporarily disable web-vitals imports to fix app loading issue
// import { getCLS, getFID, getFCP, getLCP, getTTFB } from 'web-vitals';

// Temporary fallback functions
const getCLS = (callback: any) => { console.warn('CLS measurement disabled'); };
const getFID = (callback: any) => { console.warn('FID measurement disabled'); };
const getFCP = (callback: any) => { console.warn('FCP measurement disabled'); };
const getLCP = (callback: any) => { console.warn('LCP measurement disabled'); };
const getTTFB = (callback: any) => { console.warn('TTFB measurement disabled'); };

interface Metric {
  value: number;
  name: string;
}

interface PerformanceMetrics {
  // Core Web Vitals
  LCP?: number; // Largest Contentful Paint
  FID?: number; // First Input Delay  
  CLS?: number; // Cumulative Layout Shift
  
  // Additional Web Vitals
  FCP?: number; // First Contentful Paint
  TTFB?: number; // Time to First Byte
  
  // Custom metrics
  TTI?: number; // Time to Interactive
  FMP?: number; // First Meaningful Paint
  navigationTiming?: PerformanceNavigationTiming;
  
  // User context
  connection?: string;
  deviceType?: string;
  userAgent?: string;
  timestamp: number;
  sessionId: string;
  userId?: string;
  page: string;
}

interface PerformanceThresholds {
  LCP: { good: number; needsImprovement: number };
  FID: { good: number; needsImprovement: number };
  CLS: { good: number; needsImprovement: number };
  FCP: { good: number; needsImprovement: number };
  TTFB: { good: number; needsImprovement: number };
}

class PerformanceMonitor {
  private metrics: PerformanceMetrics;
  private thresholds: PerformanceThresholds;
  private sessionId: string;
  private isMonitoring: boolean = false;
  private callbacks: Array<(metrics: PerformanceMetrics) => void> = [];

  constructor() {
    this.sessionId = this.generateSessionId();
    this.metrics = {
      timestamp: Date.now(),
      sessionId: this.sessionId,
      page: window.location.pathname
    };

    // Google's recommended thresholds
    this.thresholds = {
      LCP: { good: 2500, needsImprovement: 4000 },
      FID: { good: 100, needsImprovement: 300 },
      CLS: { good: 0.1, needsImprovement: 0.25 },
      FCP: { good: 1800, needsImprovement: 3000 },
      TTFB: { good: 800, needsImprovement: 1800 }
    };

    this.collectDeviceInfo();
  }

  /**
   * Start monitoring performance metrics
   */
  startMonitoring(): void {
    if (this.isMonitoring) return;
    
    this.isMonitoring = true;
    // console.log('🚀 Performance monitoring started'); // DISABLED TO STOP SPAM

    // Collect Core Web Vitals
    this.collectCoreWebVitals();
    
    // Collect additional metrics
    this.collectNavigationTiming();
    this.collectCustomMetrics();
    
    // Set up periodic reporting
    this.setupPeriodicReporting();
    
    // Monitor for page visibility changes
    this.setupVisibilityChangeTracking();
    
    // Monitor for errors that might affect performance
    this.setupErrorTracking();
  }

  /**
   * Stop monitoring
   */
  stopMonitoring(): void {
    this.isMonitoring = false;
    console.log('⏹️ Performance monitoring stopped');
  }

  /**
   * Add callback for metric updates
   */
  onMetricsUpdate(callback: (metrics: PerformanceMetrics) => void): void {
    this.callbacks.push(callback);
  }

  /**
   * Get current metrics
   */
  getMetrics(): PerformanceMetrics {
    return { ...this.metrics };
  }

  /**
   * Get performance score (0-100)
   */
  getPerformanceScore(): number {
    const scores: number[] = [];
    
    if (this.metrics.LCP !== undefined) {
      scores.push(this.calculateMetricScore('LCP', this.metrics.LCP));
    }
    
    if (this.metrics.FID !== undefined) {
      scores.push(this.calculateMetricScore('FID', this.metrics.FID));
    }
    
    if (this.metrics.CLS !== undefined) {
      scores.push(this.calculateMetricScore('CLS', this.metrics.CLS));
    }

    if (scores.length === 0) return 0;
    
    return Math.round(scores.reduce((sum, score) => sum + score, 0) / scores.length);
  }

  /**
   * Check if metrics meet performance budgets
   */
  checkPerformanceBudgets(): { [key: string]: 'good' | 'needs-improvement' | 'poor' } {
    const results: { [key: string]: 'good' | 'needs-improvement' | 'poor' } = {};
    
    Object.entries(this.thresholds).forEach(([metric, thresholds]) => {
      const value = this.metrics[metric as keyof PerformanceMetrics] as number;
      
      if (value !== undefined) {
        if (value <= thresholds.good) {
          results[metric] = 'good';
        } else if (value <= thresholds.needsImprovement) {
          results[metric] = 'needs-improvement';
        } else {
          results[metric] = 'poor';
        }
      }
    });
    
    return results;
  }

  /**
   * Export metrics for analysis
   */
  exportMetrics(): string {
    const exportData = {
      ...this.metrics,
      performanceScore: this.getPerformanceScore(),
      budgetResults: this.checkPerformanceBudgets(),
      thresholds: this.thresholds,
      exportedAt: new Date().toISOString()
    };
    
    return JSON.stringify(exportData, null, 2);
  }

  /**
   * Send metrics to analytics service
   */
  async sendToAnalytics(_endpoint: string = '/api/analytics/performance'): Promise<void> {
    // No-op: backend does not have /api/analytics/performance endpoint.
    // Metrics remain available in-memory via getPerformanceScore() and exportMetrics().
  }

  /**
   * Collect Core Web Vitals
   */
  private collectCoreWebVitals(): void {
    // Largest Contentful Paint
    getLCP((metric: Metric) => {
      this.metrics.LCP = metric.value;
      this.onMetricUpdate('LCP', metric.value);
    });

    // First Input Delay
    getFID((metric: Metric) => {
      this.metrics.FID = metric.value;
      this.onMetricUpdate('FID', metric.value);
    });

    // Cumulative Layout Shift
    getCLS((metric: Metric) => {
      this.metrics.CLS = metric.value;
      this.onMetricUpdate('CLS', metric.value);
    });

    // First Contentful Paint
    getFCP((metric: Metric) => {
      this.metrics.FCP = metric.value;
      this.onMetricUpdate('FCP', metric.value);
    });

    // Time to First Byte
    getTTFB((metric: Metric) => {
      this.metrics.TTFB = metric.value;
      this.onMetricUpdate('TTFB', metric.value);
    });
  }

  /**
   * Collect Navigation Timing API data
   */
  private collectNavigationTiming(): void {
    if ('performance' in window && 'getEntriesByType' in performance) {
      window.addEventListener('load', () => {
        setTimeout(() => {
          const navTiming = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
          
          if (navTiming) {
            this.metrics.navigationTiming = navTiming;
            
            // Calculate Time to Interactive (approximate)
            this.metrics.TTI = navTiming.domInteractive - navTiming.fetchStart;
            
            this.updateCallbacks();
          }
        }, 0);
      });
    }
  }

  /**
   * Collect custom performance metrics
   */
  private collectCustomMetrics(): void {
    // Monitor resource loading
    this.monitorResourcePerformance();
    
    // Monitor React rendering performance
    this.monitorReactPerformance();
    
    // Monitor API response times
    this.monitorAPIPerformance();
  }

  /**
   * Monitor resource loading performance
   */
  private monitorResourcePerformance(): void {
    if ('performance' in window) {
      window.addEventListener('load', () => {
        const resources = performance.getEntriesByType('resource');
        
        const slowResources = resources.filter((resource: any) => 
          resource.duration > 1000 // Resources taking more than 1 second
        );
        
        if (slowResources.length > 0) {
          console.warn('🐌 Slow loading resources detected:', slowResources);
          
          // Log to analytics
          this.logPerformanceIssue('slow-resources', {
            count: slowResources.length,
            resources: slowResources.map((r: any) => ({
              name: r.name,
              duration: r.duration,
              type: r.initiatorType
            }))
          });
        }
      });
    }
  }

  /**
   * Monitor React rendering performance
   */
  private monitorReactPerformance(): void {
    // Use React DevTools profiling in development
    if (process.env.NODE_ENV === 'development') {
      if ('__REACT_DEVTOOLS_GLOBAL_HOOK__' in window) {
        console.log('🔧 React DevTools detected - enable Profiler for detailed insights');
      }
    }

    // Monitor for long React renders using Performance Observer
    if ('PerformanceObserver' in window) {
      try {
        const observer = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          
          entries.forEach((entry) => {
            if (entry.duration > 16) { // More than one frame (60fps)
              console.warn('🐌 Long React render detected:', {
                name: entry.name,
                duration: entry.duration,
                startTime: entry.startTime
              });
            }
          });
        });
        
        observer.observe({ entryTypes: ['measure'] });
      } catch (error) {
        console.log('Performance Observer not fully supported');
      }
    }
  }

  /**
   * Monitor API response times
   */
  private monitorAPIPerformance(): void {
    // Intercept fetch API
    const originalFetch = window.fetch;
    
    window.fetch = async (...args) => {
      const start = performance.now();
      const url = typeof args[0] === 'string' ? args[0] : (args[0] as Request).url;
      
      try {
        const response = await originalFetch(...args);
        const duration = performance.now() - start;
        
        // Log slow API calls
        if (duration > 2000) { // Slower than 2 seconds
          console.warn('🐌 Slow API call detected:', {
            url,
            duration: Math.round(duration),
            status: response.status
          });
          
          this.logPerformanceIssue('slow-api', {
            url,
            duration,
            status: response.status
          });
        }
        
        return response;
      } catch (error) {
        const duration = performance.now() - start;
        
        this.logPerformanceIssue('api-error', {
          url,
          duration,
          error: (error as Error).message
        });
        
        throw error;
      }
    };
  }

  /**
   * Set up periodic reporting
   */
  private setupPeriodicReporting(): void {
    // Report metrics every 30 seconds
    setInterval(() => {
      if (this.isMonitoring && document.visibilityState === 'visible') {
        this.sendToAnalytics().catch(() => {
          // Silently fail - analytics shouldn't break the app
        });
      }
    }, 30000);
  }

  /**
   * Track page visibility changes
   */
  private setupVisibilityChangeTracking(): void {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        // Send final metrics before page becomes hidden
        this.sendToAnalytics().catch(() => {});
      }
    });
  }

  /**
   * Set up error tracking that might affect performance
   */
  private setupErrorTracking(): void {
    window.addEventListener('error', (event) => {
      this.logPerformanceIssue('javascript-error', {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno
      });
    });

    window.addEventListener('unhandledrejection', (event) => {
      this.logPerformanceIssue('unhandled-promise', {
        reason: event.reason
      });
    });
  }

  /**
   * Collect device and connection information
   */
  private collectDeviceInfo(): void {
    // Connection information
    const connection = (navigator as any).connection || 
                      (navigator as any).mozConnection || 
                      (navigator as any).webkitConnection;
    
    if (connection) {
      this.metrics.connection = `${connection.effectiveType} (${connection.downlink}Mbps)`;
    }

    // Device type estimation
    const userAgent = navigator.userAgent;
    this.metrics.userAgent = userAgent;
    
    if (/Mobile|Android|iPhone|iPad/.test(userAgent)) {
      this.metrics.deviceType = 'mobile';
    } else if (/Tablet/.test(userAgent)) {
      this.metrics.deviceType = 'tablet';
    } else {
      this.metrics.deviceType = 'desktop';
    }
  }

  /**
   * Calculate metric score (0-100)
   */
  private calculateMetricScore(metric: keyof PerformanceThresholds, value: number): number {
    const threshold = this.thresholds[metric];
    
    if (value <= threshold.good) {
      return 100;
    } else if (value <= threshold.needsImprovement) {
      return 75;
    } else {
      return 50;
    }
  }

  /**
   * Handle metric updates
   */
  private onMetricUpdate(name: string, value: number): void {
    console.log(`📊 ${name}: ${value.toFixed(2)}ms`);
    
    // Check if metric exceeds threshold
    const budget = this.checkPerformanceBudgets();
    const metricBudget = budget[name];
    
    if (metricBudget === 'poor') {
      console.warn(`⚠️ ${name} exceeds recommended threshold:`, {
        value,
        threshold: this.thresholds[name as keyof PerformanceThresholds],
        recommendation: this.getOptimizationTip(name as keyof PerformanceThresholds)
      });
    }
    
    this.updateCallbacks();
  }

  /**
   * Update all registered callbacks
   */
  private updateCallbacks(): void {
    this.callbacks.forEach(callback => {
      try {
        callback(this.metrics);
      } catch (error) {
        console.error('Error in performance callback:', error);
      }
    });
  }

  /**
   * Log performance issues
   */
  private logPerformanceIssue(type: string, data: any): void {
    console.warn(`⚠️ Performance issue (${type}):`, data);
    
    // Could send to error tracking service
    // Sentry, LogRocket, etc.
  }

  /**
   * Get optimization tips
   */
  private getOptimizationTip(metric: keyof PerformanceThresholds): string {
    const tips = {
      LCP: 'Optimize largest content element: compress images, use modern formats (WebP/AVIF), implement lazy loading',
      FID: 'Reduce JavaScript execution time: code splitting, defer non-critical JS, optimize event handlers',
      CLS: 'Prevent layout shifts: set dimensions for images/videos, reserve space for dynamic content',
      FCP: 'Optimize critical rendering path: inline critical CSS, preload key resources, reduce server response time',
      TTFB: 'Improve server response time: optimize database queries, use CDN, enable caching'
    };
    
    return tips[metric] || 'General optimization: minimize bundle size, enable compression, use performance best practices';
  }

  /**
   * Generate session ID
   */
  private generateSessionId(): string {
    return `perf_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
}

// Singleton instance
export const performanceMonitor = new PerformanceMonitor();

// Convenience functions
export const startPerformanceMonitoring = () => performanceMonitor.startMonitoring();
export const getPerformanceMetrics = () => performanceMonitor.getMetrics();
export const getPerformanceScore = () => performanceMonitor.getPerformanceScore();
export const checkPerformanceBudgets = () => performanceMonitor.checkPerformanceBudgets();
export const exportPerformanceData = () => performanceMonitor.exportMetrics();

// Auto-start in production
if (process.env.NODE_ENV === 'production' && typeof window !== 'undefined') {
  // Start monitoring after page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      setTimeout(startPerformanceMonitoring, 1000);
    });
  } else {
    setTimeout(startPerformanceMonitoring, 1000);
  }
}

// Types
export type { PerformanceMetrics, PerformanceThresholds };